import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { DashboardHeader } from '../../organisms/DashboardHeader';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { StoryLayoutHeroContext } from '@/context/StoryLayoutHeroContext';
import styles from './StoryLayout.module.scss';
import type { StoryLayoutProps } from './StoryLayout.types';

/** Eyebrow de categoría bajo el hero (ChallengePage fase recursos); ancla el degradado del header al salir la imagen */
const CATEGORY_EYEBROW_SELECTOR = '[data-story-category]';
/** Pixels below header bottom sin degradado fuerte; luego fundido hasta el borde del header */
const HEADER_BACKDROP_FADE_BAND_PX = 120;

function computeHeaderBackdropOpacity(scrollRoot: HTMLElement): number {
  const anchor = scrollRoot.querySelector(CATEGORY_EYEBROW_SELECTOR);
  if (!anchor) return 1;

  const header = scrollRoot.querySelector('header');
  const headerBottom = header?.getBoundingClientRect().bottom ?? 88;
  const fadeStart = headerBottom + HEADER_BACKDROP_FADE_BAND_PX;
  const top = anchor.getBoundingClientRect().top;

  if (top >= fadeStart) return 0;
  if (top <= headerBottom) return 1;
  return (fadeStart - top) / (fadeStart - headerBottom);
}

/** Ancla al final del bloque intro (objetivo/descripción) para fundir el degradé inferior como el header */
const INTRO_END_SELECTOR = '[data-story-intro-end]';
/** Zona inferior del viewport: al subir el final del intro, el degradé baja de opacidad */
const BOTTOM_FADE_CLEAR_BAND_PX = 200;

function computeBottomBodyFadeOpacity(scrollRoot: HTMLElement): number {
  const anchor = scrollRoot.querySelector(INTRO_END_SELECTOR);
  if (!anchor) return 1;

  const anchorBottom = anchor.getBoundingClientRect().bottom;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  if (vh <= 0) return 1;

  const fadeOutY = vh - BOTTOM_FADE_CLEAR_BAND_PX;
  const fadeFullY = vh - 72;

  if (anchorBottom <= fadeOutY) return 0;
  if (anchorBottom >= fadeFullY) return 1;
  return (anchorBottom - fadeOutY) / (fadeFullY - fadeOutY);
}

/**
 * StoryLayout — Independent layout for story and result pages.
 *
 * Unlike StudentDashboardLayout, this layout:
 * - Has no sidebar
 * - Uses height: 100svh + overflow: hidden so scroll is contained inside __body
 * - Places the header in normal document flow (not position: fixed)
 * - Optional hero slot (StoryPage setHero) full-bleed con 8px de inset; header por encima
 * - Manages its own bottom bar
 *
 * This eliminates scroll/padding conflicts with StudentDashboardLayout.
 */
export function StoryLayout({
  userName,
  userInitials,
  xp,
  notificationCount,
  onNotificationClick,
  onCloseClick,
  onSavedItemsClick,
  onProfileClick,
  onSettingsClick,
  onLogout,
  onDropdownOpen,
  children,
  className,
}: StoryLayoutProps) {
  const location = useLocation();
  /** Evita que la zona de scroll se estire a toda la pantalla con poco contenido (intro «Empezar»). */
  const hugMainContentHeight = /^\/student\/conversations\/[^/]+$/.test(location.pathname);

  const { content: bottomBarContent, hideFade, hideCloseButton, headerProgress } = useStoryBottomBar() ?? { content: null, hideFade: false, hideCloseButton: false, headerProgress: null };

  const [hero, setHeroState] = useState<ReactNode>(null);
  const setHero = useCallback((node: ReactNode | null) => {
    setHeroState(node);
  }, []);
  const heroContext = useMemo(() => ({ hero, setHero }), [hero, setHero]);

  const hasHero = Boolean(hero);

  const bodyRef = useRef<HTMLElement>(null);
  const [backdropGradientOpacity, setBackdropGradientOpacity] = useState(1);
  const [bottomFadeOpacity, setBottomFadeOpacity] = useState(1);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    let raf = 0;
    const scheduleUpdate = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setBackdropGradientOpacity(computeHeaderBackdropOpacity(el));
        setBottomFadeOpacity(computeBottomBodyFadeOpacity(el));
      });
    };

    scheduleUpdate();

    el.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(el);

    const mo = new MutationObserver(scheduleUpdate);
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      ro.disconnect();
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hero]);

  return (
    <StoryLayoutHeroContext.Provider value={heroContext}>
      <div className={cn(styles.storyLayout, className)}>
        {/* Scroll container — header is sticky inside so content scrolls under its gradient */}
        <main
          ref={bodyRef}
          className={styles.storyLayout__body}
          data-story-scroll-container=""
        >
          <DashboardHeader
            variant="contained"
            compactOnMobile={true}
            userName={userName}
            userInitials={userInitials}
            xp={xp}
            notificationCount={notificationCount}
            onNotificationClick={onNotificationClick}
            onCloseClick={hideCloseButton ? undefined : onCloseClick}
            onSavedItemsClick={onSavedItemsClick}
            onProfileClick={onProfileClick}
            onSettingsClick={onSettingsClick}
            onLogout={onLogout}
            onDropdownOpen={onDropdownOpen}
            className={hasHero ? styles.storyLayout__headerAboveHero : undefined}
            backdropGradientOpacity={backdropGradientOpacity}
            progress={headerProgress ?? undefined}
          />

          <div
            className={styles.storyLayout__heroMount}
            data-has-hero={hasHero ? '' : undefined}
          >
            {hero}
          </div>

          <div
            className={cn(
              styles.storyLayout__content,
              hugMainContentHeight && styles.storyLayout__contentHug
            )}
            data-has-bottom-bar={bottomBarContent ? '' : undefined}
            data-has-hero={hasHero ? '' : undefined}
          >
            {children}
          </div>
        </main>

      {/* Bottom bar — in-flow, with gradient overlay that fades into body */}
      {bottomBarContent && (
        <div className={styles.storyLayout__bottomBar}>
          <div className={styles.storyLayout__bottomBarInner}>{bottomBarContent}</div>
        </div>
      )}
      {/* Bottom fade — hidden during activities phase */}
      {!hideFade && (
        <div
          className={styles.storyLayout__bodyFade}
          style={{ opacity: bottomFadeOpacity }}
          aria-hidden="true"
        />
      )}
      </div>
    </StoryLayoutHeroContext.Provider>
  );
}

StoryLayout.displayName = 'StoryLayout';
